import { useEffect, useMemo, useRef } from "react";
import { useDragLayer, XYCoord } from "react-dnd";

type AutoScrollOptions = {
  /** px from edge to start scrolling */
  threshold?: number;
  /** max px per frame (roughly; depends on rAF) */
  maxSpeed?: number;
  /** curve exponent; higher = slower until very near edge */
  power?: number;
  /** set true if you want horizontal scrolling too */
  horizontal?: boolean;
  /** element to scroll; defaults to document.scrollingElement */
  getScrollElement?: () => HTMLElement | null;
};

function clamp(n: number, min: number, max: number) {
  return Math.max(min, Math.min(max, n));
}

/**
 * Returns scroll delta (px/frame) given pointer position relative to viewport.
 */
function calcDelta(
  pos: number,
  size: number,
  threshold: number,
  maxSpeed: number,
  power: number
) {
  // Near top/left => negative scroll
  if (pos < threshold) {
    const t = clamp((threshold - pos) / threshold, 0, 1);
    return -Math.round(Math.pow(t, power) * maxSpeed);
  }

  // Near bottom/right => positive scroll
  if (pos > size - threshold) {
    const t = clamp((pos - (size - threshold)) / threshold, 0, 1);
    return Math.round(Math.pow(t, power) * maxSpeed);
  }

  return 0;
}

export function useAutoScrollWhileDragging(options?: AutoScrollOptions) {
  const {
    threshold = 80,
    maxSpeed = 24,
    power = 1.6,
    horizontal = false,
    getScrollElement = () => (document.scrollingElement as HTMLElement | null),
  } = options ?? {};

  // react-dnd gives us client offsets while dragging
  const { isDragging, clientOffset } = useDragLayer((monitor) => ({
    isDragging: monitor.isDragging(),
    clientOffset: monitor.getClientOffset(), // viewport coords
  }));

  const rafId = useRef<number | null>(null);
  const lastOffset = useRef<XYCoord | null>(null);

  const opts = useMemo(
    () => ({ threshold, maxSpeed, power, horizontal, getScrollElement }),
    [threshold, maxSpeed, power, horizontal, getScrollElement]
  );

  useEffect(() => {
    lastOffset.current = clientOffset ?? null;
  }, [clientOffset]);

  useEffect(() => {
    if (!isDragging) {
      if (rafId.current != null) cancelAnimationFrame(rafId.current);
      rafId.current = null;
      return;
    }

    const tick = () => {
      const el = opts.getScrollElement();
      const offset = lastOffset.current;

      if (el && offset) {
        const vw = window.innerWidth;
        const vh = window.innerHeight;

        const dy = calcDelta(offset.y, vh, opts.threshold, opts.maxSpeed, opts.power);
        const dx = opts.horizontal
          ? calcDelta(offset.x, vw, opts.threshold, opts.maxSpeed, opts.power)
          : 0;

        if (dx !== 0 || dy !== 0) {
          // Scroll the default document scroller
          el.scrollBy({ top: dy, left: dx, behavior: "auto" });
        }
      }

      rafId.current = requestAnimationFrame(tick);
    };

    rafId.current = requestAnimationFrame(tick);

    return () => {
      if (rafId.current != null) cancelAnimationFrame(rafId.current);
      rafId.current = null;
    };
  }, [isDragging, opts]);
}
